import React, { useState, useEffect } from "react";
import { Rocket } from "lucide-react";
import { getValidUrl, hasCustomImage, endSession } from "../utils/Utils";
import ImageField from "./ImageField";
import GalleryField from "./GalleryField";
import { PROJECT_CATEGORIES } from "../utils/categories";

const baseUrl = process.env.REACT_APP_API_URL || "http://localhost:3005";

const ProjectCard = ({ isOpen, onClose, project, mode }) => {
  const [projectsList, setProjectsList] = useState([]);

  const [formData, setFormData] = useState({
    nombre: "",
    website: "",
    imagen: "",
    galeria: [],
    descripcion: "",
    categoria: "",
    role: "",
  });

  const [step, setStep] = useState("choose"); // "choose", "form"
  const [isNewProject, setIsNewProject] = useState(false); // true → crear nuevo, false → unirse

  const categorias = PROJECT_CATEGORIES;

  useEffect(() => {

    // Cargar lista de proyectos para el dropdown
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${baseUrl}/project/`);
        const data = await response.json();

        if (response.ok) {
          // Guardamos solo _id y name
          const simplifiedProjects = (data || []).map(p => ({
            _id: p._id,
            name: p.name,
          }));
          console.log("Proyectos cargados:", simplifiedProjects);
          setProjectsList(simplifiedProjects);
        } else {
          console.error("Error al cargar proyectos:", data.message);
        }
      } catch (error) {
        console.error("Error al cargar proyectos:", error);
      }
    };
    fetchProjects();

    if (project && isOpen) {
      setFormData({
        nombre: project.name || "",
        website: project.website || "",
        imagen: hasCustomImage(project.projectImage) ? project.projectImage : "",
        galeria: project.gallery || [],
        descripcion: project.description || "",
        categoria: project.category || "",
        role: project.role || "",
      });
      setStep("form");
    } else if (isOpen) {
      setFormData({
        nombre: "",
        website: "",
        imagen: "",
        galeria: [],
        descripcion: "",
        categoria: "",
        role: "",
      });
      if (mode === "create" || mode === "join") {
        setIsNewProject(mode === "create");
        setStep("form");
      } else {
        setStep("choose");
      }
    }
  }, [project, isOpen, mode]);

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
  };

  const saveprojectData = async (projectData) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        alert("No estás autenticado. Por favor, inicia sesión.");
        window.location.href = "/";
        return null;
      }

      const method = project ? "PUT" : "POST";
      const url = project
        ? `${baseUrl}/project/${project._id}`
        : `${baseUrl}/project/`;

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(projectData),
      });

      const data = await response.json();
      if (response.status === 401) {
        endSession();
        return null;
      }
      if (!response.ok) {
        alert(data.message || "Error al guardar el proyecto");
        return null;
      }
      return data;
    } catch (error) {
      console.error("Error al guardar el proyecto:", error);
      alert("Error al guardar el proyecto");
      return null;
    }
  };

  const saveData = async () => {
    const userId = localStorage.getItem("userId");
    if (!userId) {
      alert("No se encontró el usuario.");
      return;
    }

    const projectData = {
      name: formData.nombre,
      // Un archivo subido viaja en base64 y lo sube el backend; una URL escrita
      // a mano se normaliza. Vacío a propósito si no hay nada: el front pinta
      // la portada con las iniciales
      projectImage: formData.imagen.startsWith("data:")
        ? formData.imagen
        : getValidUrl(formData.imagen.trim()) || "",
      // Cada imagen de la galería sigue el mismo camino que la principal
      gallery: formData.galeria.map((image) =>
        image.startsWith("data:") ? image : getValidUrl(image.trim()) || ""
      ).filter(Boolean),
      description: formData.descripcion,
      website: formData.website,
      category: formData.categoria,
    };

    // Guardamos el proyecto (POST o PUT)
    const savedproject = await saveprojectData(projectData);
    if (!savedproject) return;

    // Si es edición
    if (project) {
      alert("Proyecto actualizado con éxito");
      window.location.reload();
      return;
    }

    // Si es nuevo proyecto → vincular al usuario
    if (isNewProject) {
      const projectId = savedproject.project._id;
      const body = { role: formData.role, projectId };
      try {
        const response = await fetch(`${baseUrl}/users/addProject/${userId}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const data = await response.json();
        if (!response.ok) {
          alert(data.message || "Error al guardar el proyecto personal");
          return;
        }
        alert("Proyecto guardado con éxito");
        window.location.reload();
      } catch (error) {
        console.error("Error al guardar el proyecto personal:", error);
        alert("Error al guardar el proyecto personal");
      }
    } else {
      // Si se unió a uno existente, solo actualizar rol
      alert("Te has unido al proyecto con éxito");
      window.location.reload();
    }
  };

  const handleSubmitAddProject = () => {
    if (
      !formData.nombre || // para nuevo proyecto, es el nombre; para unirse, es el id del proyecto
      (!project && !formData.role) // el rol solo aplica al crear/unirse, no al editar
    ) {
      alert("Por favor, completa todos los campos.");
      return;
    }
    saveData();
    onClose();
  }

  if (!isOpen) return null;

  return (
    <div
      className="dyd-overlay"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="dyd-modal"
      >
        {step === "choose" && (
          <>
            <div className="dyd-modal-head">
          <span className="dyd-modal-icon"><Rocket size={20} /></span>
          <h2>¿Qué quieres hacer?</h2>
        </div>
            <button
              onClick={() => { setIsNewProject(true); setStep("form"); }}
              className="dyd-btn dyd-btn-primary"
            >
              Crear proyecto nuevo
            </button>
            <button
              onClick={() => { setIsNewProject(false); setStep("form"); }}
              className="dyd-btn dyd-btn-ghost"
            >
              Añadirse a un proyecto existente
            </button>
            <button
              onClick={onClose}
              className="dyd-btn dyd-btn-subtle"
            >
              Cerrar
            </button>
          </>
        )}

        {step === "form" && (
          <>
            <div className="dyd-modal-head">
          <span className="dyd-modal-icon"><Rocket size={20} /></span>
          <h2>
              {project
                ? "Editar proyecto"
                : isNewProject
                  ? "Crear proyecto"
                  : "Unirse a proyecto existente"}
            </h2>
        </div>

            {isNewProject || project ? (
              <>
                {/* Formulario normal para crear o editar */}
                <div className="dyd-field">
                  <label className="dyd-label">Nombre</label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={handleChange("nombre")}
                    placeholder="Nombre del proyecto"
                    className="dyd-input"
                  />
                </div>

                <div className="dyd-field">
                  <label className="dyd-label">Website</label>
                  <input
                    type="text"
                    value={formData.website}
                    onChange={handleChange("website")}
                    placeholder="Página web"
                    className="dyd-input"
                  />
                </div>

                <ImageField
                  label="Imagen del proyecto (opcional)"
                  value={formData.imagen}
                  onChange={(imagen) => setFormData((f) => ({ ...f, imagen }))}
                  hint="Si no pones ninguna, se usará una portada con las iniciales del proyecto."
                />

                <GalleryField
                  value={formData.galeria}
                  onChange={(galeria) => setFormData((f) => ({ ...f, galeria }))}
                />

                <div className="dyd-field">
                  <label className="dyd-label">Descripción</label>
                  <textarea
                    value={formData.descripcion}
                    onChange={handleChange("descripcion")}
                    placeholder="Breve descripción"
                    rows={3}
                    className="dyd-textarea"
                  />
                </div>

                <div className="dyd-field">
                  <label className="dyd-label">Categoría</label>
                  <select
                    value={formData.categoria}
                    onChange={handleChange("categoria")}
                    className="dyd-select"
                  >
                    <option value="">Selecciona una categoría</option>
                    {categorias.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                {!project && (
                  <div className="dyd-field">
                    <label className="dyd-label">Rol</label>
                    <input
                      type="text"
                      value={formData.role}
                      onChange={handleChange("role")}
                      placeholder="Indica tu rol en el proyecto"
                      className="dyd-input"
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Dropdown para unirse a proyecto existente */}
                <div className="dyd-field">
                  <label className="dyd-label">Selecciona un proyecto</label>
                  <select
                    value={formData.nombre} // usamos "nombre" para almacenar el id o nombre del proyecto
                    onChange={handleChange("nombre")}
                    className="dyd-select"
                  >
                    <option value="">-- Selecciona un proyecto --</option>
                    {projectsList.map((p) => (
                      <option key={p._id} value={p._id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="dyd-field">
                  <label className="dyd-label">Rol en el proyecto</label>
                  <input
                    type="text"
                    value={formData.role}
                    onChange={handleChange("role")}
                    placeholder="Tu rol dentro del proyecto"
                    className="dyd-input"
                  />
                </div>
              </>
            )}

            <button
              onClick={handleSubmitAddProject}
              className="dyd-btn dyd-btn-primary"
            >
              {project ? "Guardar cambios" : "Guardar"}
            </button>

            <button
              onClick={onClose}
              className="dyd-btn dyd-btn-subtle"
            >
              Cerrar
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectCard;
